import React, { useState } from "react";
import "./TauriDocuments.css";
import TauriImage from "../TauriImage/TauriImage";
import appURL from '../../appURL.json';
import { updateRecord } from "../../localDB";
import { FileStatus } from "../../visualizations/visualizations";
import { Spinner } from "react-bootstrap";

const fileIcons = {
    pdf: "/Images/pdf.png",
    doc: "/Images/word.png",
    docx: "/Images/word.png",
    xls: "/Images/excel.png",
    xlsx: "/Images/excel.png",
    xlsb: "/Images/excel.png",
    csv: "/Images/excel.png",
    ppt: "/Images/powerpoint.png",
    pptx: "/Images/powerpoint.png",
    txt: "/Images/text.png",
    jpg: "/Images/image-icon.png",
    jpeg: "/Images/image-icon.png",
    png: "/Images/image-icon.png",
    gif: "/Images/image-icon.png",
    bmp: "/Images/image-icon.png",
    svg: "/Images/image-icon.png",
    webp: "/Images/image-icon.png",
    mp4: "/Images/video-icon.png",
    avi: "/Images/video-icon.png",
    mkv: "/Images/video-icon.png",
    mov: "/Images/video-icon.png",
    wmv: "/Images/video-icon.png",
    flv: "/Images/video-icon.png",
    mp3: "/Images/audio-icon.png",
    wav: "/Images/audio-icon.png",
    ogg: "/Images/audio-icon.png",
    flac: "/Images/audio-icon.png",
    zip: "/Images/zip-icon.png",
    rar: "/Images/zip-icon.png",
    tar: "/Images/zip-icon.png",
    gz: "/Images/zip-icon.png",
    "7z": "/Images/zip-icon.png",
    js: "/Images/code-icon.png",
    json: "/Images/code-icon.png",
    html: "/Images/code-icon.png",
    css: "/Images/code-icon.png",
    php: "/Images/code-icon.png",
    py: "/Images/code-icon.png",
    java: "/Images/code-icon.png",
    c: "/Images/code-icon.png",
    cpp: "/Images/code-icon.png",
    ts: "/Images/code-icon.png",
    default: "/Images/file.png",
};

const getFileExtension = (filenameOrExt) => {
    if (!filenameOrExt) return "";
    const ext = filenameOrExt.includes(".")
        ? filenameOrExt.split(".").pop()
        : filenameOrExt;
    return ext.toLowerCase();
};

const TauriDocuments = ({
    doc,
    handleDeleteClick,
    handleClick, // ✅ now passed in props
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [status, setStatus] = useState(doc?.Status);

    const fileExt = getFileExtension(doc.Extension || doc.FileName);
    const icon = fileIcons[fileExt] || fileIcons.default;

    if (!doc) {
        return <div className="no-documents">No document available</div>;
    }

    const handleDownloadClick = async () => {
        const isOnline = JSON.parse(localStorage.getItem("isOnline"));
        const hasInternet = navigator.onLine;

        if (!isOnline || !hasInternet) return;

        if (!doc?.AccessUrl) {
            console.error("Document URL is missing.");
            return;
        }

        try {
            setIsDownloading(true);
            const response = await fetch(`${appURL.APP_BaseURL}${doc.AccessUrl}`);
            if (!response.ok) throw new Error("Network response was not ok");

            const blob = await response.blob();

            if (doc?.SizeClassification === "small") {
                // Save to IndexedDB as Blob
                const updatedRecord = {
                    ...doc,
                    BlobData: blob, // store raw blob
                    Status: FileStatus.downloaded,
                };
                setStatus(FileStatus.downloaded);
                await updateRecord("file", updatedRecord);
            } else if (doc?.SizeClassification === "large") {
                // Trigger download to user's device
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = doc.FileName || "download"; // fallback name
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                // Optionally update status in IndexedDB too
                const updatedRecord = {
                    ...doc,
                    Status: FileStatus.downloaded,
                };
                setStatus(FileStatus.downloaded);
                await updateRecord("file", updatedRecord);
            }
        } catch (error) {
            console.error("Failed to cache file locally:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="documents-container d-flex flex-column">
            <div
                className="document-item d-flex align-items-center gap-2"
                onClick={() => {
                    if (status !== FileStatus.downloaded) {
                        handleDownloadClick();
                    } else {
                        handleClick(doc);
                    }
                }}
            >
                <TauriImage
                    localImage={icon}
                    alt="Document Icon"
                    className="document-icon"
                />
                <div className="document-info d-flex flex-column">
                    <div className="document-title">{doc?.FileName}</div>
                    <div className="document-meta d-flex flex-wrap align-items-center">
                        {doc?.Status === 0 ? (
                            <div className="document-status">Awaiting Approval</div>
                        ) : (
                            <div className="document-dates">
                                {doc?.ApprovedDate
                                    ? new Date(doc?.ApprovedDate).toLocaleString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                    })
                                    : ""}
                            </div>
                        )}

                        {/* ✅ download icon only if file is not yet downloaded */}
                        {status !== FileStatus.downloaded && (
                            isDownloading ? (
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="ms-2"
                                />
                            ) : (
                                <TauriImage
                                    localImage={"/Images/mdi--download.svg"}
                                    alt="Download Document"
                                    className="download-icon ms-2"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadClick();
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TauriDocuments;
